import React, { useState, useEffect } from "react";
import { differenceInSeconds, add, format } from "date-fns";
import { AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaw } from "@fortawesome/free-solid-svg-icons";

const DurationTimer = ({ start_time, duration }) => {
  const [remainingTime, setRemainingTime] = useState(0);
  const [extraTime, setExtraTime] = useState(0);
  const [countingUp, setCountingUp] = useState(false);

  // Format start time in a human-friendly way
  const formattedStartTime = format(new Date(start_time), "hh:mm a");

  // Parse duration to calculate end time
  const calculateEndTime = () => {
    const startTime = new Date(start_time);
    const [amount, unit] = duration.split(" ");
    let endTime;

    switch (unit) {
      case "hour":
      case "hours":
        endTime = add(startTime, { hours: parseInt(amount, 10) });
        break;
      case "minute":
      case "minutes":
        endTime = add(startTime, { minutes: parseInt(amount, 10) });
        break;
      default:
        endTime = startTime;
    }
    return endTime;
  };

  const end_time = calculateEndTime();

  // Initialize countdown and handle switch to extra time
  useEffect(() => {
    const initialRemainingTime = differenceInSeconds(end_time, new Date());
    setRemainingTime(initialRemainingTime);

    const interval = setInterval(() => {
      const now = new Date();
      const secondsRemaining = differenceInSeconds(end_time, now);

      if (secondsRemaining <= 0) {
        setCountingUp(true);
        setRemainingTime(0); // Keep countdown at zero
        setExtraTime(Math.abs(secondsRemaining)); 
      } else {
        setRemainingTime(secondsRemaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [end_time]);

  // Increment extra time when in counting up mode
  useEffect(() => {
    if (countingUp) {
      const extraInterval = setInterval(() => {
        setExtraTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(extraInterval);
    }
  }, [countingUp]);

  const formatTimeUnits = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;

    return {
      hours: String(hours).padStart(2, "0"),
      minutes: String(minutes).padStart(2, "0"),
      seconds: String(seconds).padStart(2, "0"),
    };
  };

  const { hours, minutes, seconds } = formatTimeUnits(remainingTime);
  const { hours: extraHours, minutes: extraMinutes, seconds: extraSeconds } = formatTimeUnits(extraTime);

  return (
    <div className="flex flex-col items-center justify-center space-y-6 p-4 text-center">
      <h5 className="text-2xl text-tblue mb-2">
        🐾 Get ready! Your walk begins at{" "}
        <span className="font-bold text-purple-700">{formattedStartTime}</span>{" "}
        and lasts for{" "}
        <span className="font-bold text-purple-700">{duration}</span> of pawsome
        adventures! 🐕🌳
      </h5>

      {/* Display countdown or extra time based on countingUp state */}
      <div className="grid grid-cols-2 gap-4 items-center">
        {/* Label with animated attention effect */}
        <div className="flex space-x-4 items-center justify-center">
          <h5
            className="text-3xl font-bold text-purple-700 flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={faPaw} className="text-2xl text-pink-500" />
            <span>{countingUp ? "Walk time’s up, but the fun continues! Extra time until your pet’s on the way home! 🐾🚶‍♂️" : "Time Remaining for Walk End"}</span>
            <FontAwesomeIcon icon={faPaw} className="text-2xl text-pink-500" />
          </h5>
        </div>

        {/* Countdown or Extra Time Timer */}
        <div className="flex justify-center space-x-4 items-center">
          {["hours", "minutes", "seconds"].map((unit) => (
            <AnimatePresence key={unit} mode="wait">
              <div
                key={unit}
                className="relative bg-tblue text-white text-6xl font-bold w-24 h-24 rounded-full shadow-lg flex items-center justify-center"
              >
                <span className="absolute -top-5 -left-5 text-primary text-2xl">
                  <FontAwesomeIcon icon={faPaw} />
                </span>
                {countingUp
                  ? unit === "hours"
                    ? extraHours
                    : unit === "minutes"
                    ? extraMinutes
                    : extraSeconds
                  : unit === "hours"
                  ? hours
                  : unit === "minutes"
                  ? minutes
                  : seconds}
                <span className="absolute -bottom-5 -right-5 text-accent text-2xl rotate-45">
                  <FontAwesomeIcon icon={faPaw} />
                </span>
              </div>
            </AnimatePresence>
          ))}
        </div>
      </div>

    </div>
  );
};

export default DurationTimer;
